<template>
  <component
    :is="playerLink ? 'a' : 'div'"
    class="comet-thumbnail asset-module__image"
    :href="playerLink || undefined"
    :target="playerLink ? '_blank' : undefined"
    :class="{
      'asset-module__image--transparent': useTransparentBackground,
      'comet-thumbnail--contain': imageSettings === 'letterbox' && validThumbnailUrl,
    }"
    :referrerpolicy="playerLink ? 'no-referrer-when-downgrade' : undefined"
  >
    <!-- If we have a player link, this component (above) becomes an <a> tag.
      The referrerpolicy here is to allow better url tracking -->
    <div
      class="comet-thumbnail__inner"
      :style="{
        backgroundImage: `url('${validThumbnailUrl}')`,
      }"
      v-if="validThumbnailUrl"
    >
      <img
        :src="validThumbnailUrl"
        :alt="title"
        class="comet-thumbnail__print-only-image"
      >
      <span class="comet-screenreader-only">{{ title }}</span>
    </div>
    <LoadingIndicator
      absolute
      v-else-if="thumbnailIsLoading"
      class="asset-module__image-loading-indicator"
    />
    <SelectedResponseTEI
      v-else-if="assetSubtype === 'selected-response' && !playerLink"
      :asset-id="asset.id"
      :mode="mode"
      :is-displayed="isDisplayed"
    />
    <ConnectionsTEI
      v-else-if="assetSubtype === 'connections' && !playerLink"
      :asset-id="asset.id"
      :mode="mode"
      :scale="scale"
      :is-displayed="isDisplayed"
    />
    <TilingTEI
      v-else-if="assetSubtype === 'tiling' && !playerLink"
      :asset-id="asset.id"
      :mode="mode"
      :scale="scale"
      :is-displayed="isDisplayed"
    />
    <SelectObjectTEI
      v-else-if="assetSubtype === 'select-object' && !playerLink"
      :asset-id="asset.id"
      :mode="mode"
      :scale="scale"
      :is-displayed="isDisplayed"
    />
    <ConstructedResponseTEI
      v-else-if="assetSubtype === 'constructed-response' && !playerLink"
      :asset-id="asset.id"
      :mode="mode"
      :scale="scale"
      :is-displayed="isDisplayed"
    />
    <div
      class="comet-thumbnail__inner"
      v-else
    >
      <span
        v-if="type === 'TEI'"
        class="asset-module__tei-placeholder"
      >
        {{ $t('TEI Placeholder') }}
      </span>
      <NebulaIcon
        v-else
        :symbol-id="iconId"
        class-override="comet-thumbnail__icon"
      />
    </div>
  </component>
</template>

<script>
import { defineAsyncComponent } from 'vue';
import { NebulaIcon } from '@discoveryedu/nebula-components';
import LoadingIndicator from '@/components/LoadingIndicator.vue';
import thumbnailLoader from '@/mixins/thumbnailLoader';

// These are also loaded manually when asset data is cached, so they should be available by
// the time they need to be rendered
const SelectedResponseTEI = defineAsyncComponent(() => import('@/components/teis/SelectedResponseTEI.vue'));
const ConnectionsTEI = defineAsyncComponent(() => import('@/components/teis/ConnectionsTEI.vue'));
const SelectObjectTEI = defineAsyncComponent(() => import('@/components/teis/SelectObjectTEI.vue'));
const ConstructedResponseTEI = defineAsyncComponent(() => import('@/components/teis/ConstructedResponseTEI.vue'));
const TilingTEI = defineAsyncComponent(() => import('@/components/teis/TilingTEI.vue'));

export default {
  name: 'PlayerViewer',
  components: {
    NebulaIcon,
    LoadingIndicator,
    SelectedResponseTEI,
    ConnectionsTEI,
    SelectObjectTEI,
    ConstructedResponseTEI,
    TilingTEI,
  },
  mixins: [thumbnailLoader],
  props: {
    asset: {
      type: Object,
      default: null,
    },
    imageSettings: {
      type: String,
      default: null,
    },
    mode: {
      type: String,
      default: null,
    },
    playerLink: {
      type: String,
      default: null,
    },
    title: {
      type: String,
      default: null,
    },
    type: {
      type: String,
      default: null,
    },
    scale: {
      type: Number,
      default: 1,
    },
    useTransparentBackground: Boolean,
    isDisplayed: {
      type: Boolean,
      default: true,
    },
  },
  computed: {
    iconId() {
      if (!this.type || this.type === 'Other') {
        return 'asset-document';
      }

      const assetTypeSlug = this.type.toLowerCase().replace(/[^a-z]/gi, '-');
      return `asset-${assetTypeSlug}`;
    },
    assetSubtype() {
      // Properties are named differently in the content and asset apis
      return this.asset?.subtype?.name || this.asset?.sub_type?.name;
    },
    thumbnailLoaderDisabled() {
      return this.type === 'TEI';
    },
  },
};
</script>

<style lang="stylus">
.asset-module {
  &__image-loading-indicator {
    z-index: 100;
  }

  &__tei-placeholder {
    nebula-text-h6();
    nebula-text-semibold();
    bottom: 0;
    color: $nebula-color-white;
    height: max-content;
    left: 0;
    margin: auto;
    position: absolute;
    right: 0;
    top: 0;
    width: max-content;
  }
}
</style>
